import {
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Image,
  Flex,
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import loginBanner from 'assets/login-banner.webp';
import flag from 'assets/flag.webp';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { apiAuthSignup } from 'api/auth';
import type { AuthSignupRequest } from 'api/requestTypes';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { DIALING_CODE_IRAQ, INVALID_PHONE_NUMBER_REGEX } from 'utils/constants';
import { useEventTracking } from 'hooks/useEventTracking';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useEffect, useRef, useState } from 'react';
import { extractSignupSession, parseSignupCaptchaRequirement } from 'api/responseTypes';
import {
  readSignupCaptchaPersisted,
  writeSignupCaptchaPersisted,
} from 'utils/signupCaptchaStorage';
import { getSignupHcaptchaSiteKey } from 'utils/signupEnv';

const schema = yup
  .object({
    phone: yup
      .string()
      .matches(INVALID_PHONE_NUMBER_REGEX, 'loginModal.invalid')
      .required('loginModal.invalid'),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

interface PhoneNumberContentProps {
  onChangeCredential: (value: { phone: string; session: string }) => void;
  isLoginModalOpen?: boolean;
}

export const PhoneNumberContent = ({
  onChangeCredential,
  isLoginModalOpen = true,
}: PhoneNumberContentProps) => {
  const dialingCode = localStorage.getItem('dialingCode') || DIALING_CODE_IRAQ;
  const { formatMessage } = useIntl();
  const { eventTracking } = useEventTracking();
  const hcaptchaRef = useRef<HCaptcha | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const hcaptchaSiteKey = getSignupHcaptchaSiteKey();

  useEffect(() => {
    if (!isLoginModalOpen) {
      return;
    }
    const stored = readSignupCaptchaPersisted();
    if (stored?.requiresCaptcha === true) {
      setIsCaptchaRequired(true);
      captchaTokenRef.current = null;
      hcaptchaRef.current?.resetCaptcha();
      return;
    }
    setIsCaptchaRequired(false);
    captchaTokenRef.current = null;
    hcaptchaRef.current?.resetCaptcha();
  }, [isLoginModalOpen]);

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
  });

  const showCaptchaWidget = isCaptchaRequired && Boolean(hcaptchaSiteKey);

  const activateCaptchaGate = () => {
    captchaTokenRef.current = null;
    hcaptchaRef.current?.resetCaptcha();
    setIsCaptchaRequired(true);
    writeSignupCaptchaPersisted();
  };

  const onSubmit = async (data: FormValues) => {
    const phoneNumber = dialingCode + data.phone;

    if (isCaptchaRequired && !hcaptchaSiteKey) {
      setError('phone', {
        type: 'manual',
        message: formatMessage({
          id: 'loginModal.captchaConfigError',
          defaultMessage: 'Verification could not be loaded. Please try again later.',
        }),
      });
      return;
    }

    if (showCaptchaWidget && !captchaTokenRef.current) {
      setError('phone', {
        type: 'manual',
        message: formatMessage({
          id: 'loginModal.completeCaptchaFirst',
          defaultMessage: 'Please complete the verification above before continuing.',
        }),
      });
      return;
    }

    eventTracking.login.phoneNumberContinuePressed({ phoneNumber });

    try {
      const payload: AuthSignupRequest = { phone: phoneNumber };
      if (captchaTokenRef.current) {
        payload.captchaToken = captchaTokenRef.current;
      }

      const response = await apiAuthSignup(payload);
      const session = extractSignupSession(response);

      if (!session) {
        setError('phone', {
          type: 'manual',
          message: formatMessage({
            id: 'loginModal.signupUnexpectedResponse',
            defaultMessage: 'Something went wrong. Please try again.',
          }),
        });
        return;
      }

      const captchaMeta = parseSignupCaptchaRequirement(response);
      if (captchaMeta.required) {
        writeSignupCaptchaPersisted();
      }

      captchaTokenRef.current = null;
      hcaptchaRef.current?.resetCaptcha();
      onChangeCredential({ phone: phoneNumber, session });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        const serverMsg =
          typeof e.response.data?.message === 'string' ? e.response.data.message : '';

        if (!hcaptchaSiteKey) {
          setError('phone', {
            type: 'manual',
            message: formatMessage({
              id: 'loginModal.captchaConfigError',
              defaultMessage: 'Verification could not be loaded. Please try again later.',
            }),
          });
          return;
        }

        activateCaptchaGate();

        const errorMsg =
          serverMsg ||
          formatMessage({
            id: 'loginModal.captchaRequiredFallback',
            defaultMessage: 'Please complete the verification below.',
          });
        setError('phone', { type: 'manual', message: errorMsg });
        return;
      }

      if (axios.isAxiosError(e) && e.response?.status === 400) {
        const msg =
          typeof e.response.data?.message === 'string'
            ? e.response.data.message
            : formatMessage({
                id: 'loginModal.signupUnexpectedResponse',
                defaultMessage: 'Something went wrong. Please try again.',
              });
        setError('phone', { type: 'manual', message: msg });
      }
    }
  };

  const handleCaptchaExpire = () => {
    captchaTokenRef.current = null;
    hcaptchaRef.current?.resetCaptcha();
  };

  const handleHcaptchaError = (err: string) => {
    console.error('hCaptcha error:', err);
    setError('phone', {
      type: 'manual',
      message: formatMessage({
        id: 'loginModal.captchaLoadError',
        defaultMessage: 'Verification could not be shown. Please try again.',
      }),
    });
  };

  const handleHcaptchaVerify = (token: string) => {
    captchaTokenRef.current = token;
    clearErrors('phone');
  };

  return (
    <Box as='section' aria-labelledby='phoneNumberHeader phoneNumberSubtitle'>
      <ModalHeader>
        <Text id='phoneNumberHeader' textStyle='headlineMedium' color='dark.D900'>
          <FormattedMessage id='loginModal.title' defaultMessage={'Enter phone number'} />
        </Text>

        <Text id='phoneNumberSubtitle' mt={2} textStyle='bodySmallRegular' color='dark.D500'>
          <FormattedMessage
            id='loginModal.subtitle'
            defaultMessage={'Enter your mobile phone number to log in or create an account.'}
          />
        </Text>
      </ModalHeader>

      <ModalCloseButton size={{ base: 'sm', md: 'lg' }} />
      <ModalBody overflowY='auto' overflowX='hidden'>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <label htmlFor='phoneNumberInput'>
            <Text textStyle='bodySmallMedium' color='dark.D900' mb={1}>
              <FormattedMessage id='loginModal.phoneNumber' defaultMessage={'Phone number'} />
            </Text>
          </label>

          <InputGroup>
            <Controller
              defaultValue=''
              control={control}
              name='phone'
              render={({ field, fieldState: { error } }) => (
                <>
                  <InputLeftElement pointerEvents='none' mx={1}>
                    <Image src={flag} boxSize={6} alt='image of Iraqi flag' />
                  </InputLeftElement>
                  <InputLeftElement
                    pointerEvents='none'
                    borderLeft='1px solid #DFE1E1'
                    margin='8px 0 0 45px'
                    height={6}
                    paddingLeft='15px'
                  >
                    {dialingCode}
                  </InputLeftElement>
                  <Input
                    placeholder='7XXXXXXXXX'
                    paddingLeft='95px'
                    type='tel'
                    id='phoneNumberInput'
                    borderRadius={12}
                    color='dark.D900'
                    focusBorderColor={error ? 'red' : 'green.G500'}
                    aria-invalid={Boolean(errors.phone)}
                    aria-errormessage={!errors.phone ? undefined : 'phoneNumberError'}
                    {...field}
                  />
                </>
              )}
            />
          </InputGroup>

          {errors.phone ? (
            errors.phone.type === 'manual' ? (
              <Text textStyle='bodySmallMedium' color='red' mt={2} id='phoneNumberError'>
                {errors.phone.message}
              </Text>
            ) : (
              <Text textStyle='bodySmallMedium' color='red' mt={2} id='phoneNumberError'>
                {formatMessage({
                  id: errors.phone.message,
                  defaultMessage: 'Invalid phone number. Must start with 7 and be 10 digits.',
                })}
              </Text>
            )
          ) : null}

          {showCaptchaWidget ? (
            <Text textStyle='bodySmallMedium' color='dark.D700' mt={4} id='captchaRequiredMessage'>
              {formatMessage({
                id: 'loginModal.captchaRequiredFallback',
                defaultMessage: 'Please complete the verification below.',
              })}
            </Text>
          ) : null}

          {showCaptchaWidget ? (
            <Flex
              justifyContent='center'
              mt={4}
              width='100%'
              minH='78px'
              position='relative'
              zIndex={1}
              sx={{ '& iframe': { maxWidth: '100%' } }}
            >
              <HCaptcha
                key='login-hcaptcha'
                ref={hcaptchaRef}
                sitekey={hcaptchaSiteKey}
                onVerify={handleHcaptchaVerify}
                onExpire={handleCaptchaExpire}
                onError={handleHcaptchaError}
                sentry={false}
                reCaptchaCompat={false}
                size='normal'
                theme='light'
                tabIndex={0}
              />
            </Flex>
          ) : null}

          <Button
            aria-label='Submit the phone number'
            mt={8}
            minW='100%'
            variant='primaryLarge'
            type='submit'
            isLoading={isSubmitting}
          >
            <FormattedMessage id='loginModal.button' defaultMessage={'Continue'} />
          </Button>
        </form>
      </ModalBody>
      <ModalFooter>
        <Box position='relative'>
          <Image
            src={loginBanner}
            alt='two soccer players with arms outstretched'
            w={{ base: 'fit', md: 460 }}
            h={{ base: '167px', md: 139 }}
            objectFit={{ base: 'cover', md: 'unset' }}
            borderBottomRadius='16px'
          />
          <Flex
            position='absolute'
            top='0'
            right='0'
            width='60%'
            height={{ base: '167px', md: '100%' }}
            justifyContent='center'
            direction='column'
            px={{ base: '16px', md: '40px' }}
          >
            <Text textStyle='subtitleLarge' color='white' mb={1}>
              <FormattedMessage
                id='loginModal.bannerTitle'
                defaultMessage={'Buy tickets for your favourite matches with us'}
              />
            </Text>
            <Text textStyle='bodySmallRegular' color='white' mb={1}>
              <FormattedMessage
                id='loginModal.bannerSubtitle'
                defaultMessage={'With us, buying tickets is convenient and easy'}
              />
            </Text>
          </Flex>
        </Box>
      </ModalFooter>
    </Box>
  );
};
